import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Store, MapPin, Phone, Mail, Calendar, Package, 
  TrendingUp, ExternalLink, Plus, Search, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

export default function SalesRestaurants() {
  const { api, user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await api.get('/restaurants');
      // Filter to only show restaurants onboarded by this salesperson
      const myRestaurants = response.data.filter(r => r.sales_person_id === user?.id);
      setRestaurants(myRestaurants);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRestaurants = restaurants.filter(r => 
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.cuisine_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-restaurants">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Restaurants</h1>
          <p className="text-slate-500 mt-1">Restaurants you've onboarded to Hambre</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search restaurants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
              data-testid="search-restaurants"
            />
          </div>
          <Link to="/sales/restaurants/add">
            <Button className="bg-purple-500 hover:bg-purple-600" data-testid="add-restaurant-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Restaurant
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-purple-600">{restaurants.length}</p>
            <p className="text-sm text-slate-500">Total Restaurants</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-green-600">{restaurants.filter(r => r.is_active).length}</p>
            <p className="text-sm text-slate-500">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-600">{restaurants.reduce((sum, r) => sum + (r.order_count || 0), 0)}</p>
            <p className="text-sm text-slate-500">Total Orders</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-600">{restaurants.reduce((sum, r) => sum + (r.delivery_order_count || 0), 0)}</p>
            <p className="text-sm text-slate-500">Delivery Orders</p>
          </CardContent>
        </Card>
      </div>

      {/* Restaurants List */}
      {filteredRestaurants.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Store className="w-16 h-16 mx-auto text-slate-200 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {restaurants.length === 0 ? 'No restaurants yet' : 'No matching restaurants'}
            </h3>
            <p className="text-slate-500 mb-4">
              {restaurants.length === 0 
                ? 'Start onboarding restaurants to earn commission'
                : 'Try adjusting your search query'}
            </p>
            {restaurants.length === 0 && (
              <>
                <Link to="/sales/restaurants/add">
                  <Button className="bg-purple-500 hover:bg-purple-600 mb-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Restaurant
                  </Button>
                </Link>
                <p className="text-sm text-slate-400">
                  Or share your referral code: <span className="font-mono font-bold text-purple-600">{user?.referral_code}</span>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredRestaurants.map(restaurant => (
            <Card key={restaurant.id} className="overflow-hidden hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      {restaurant.image_url ? (
                        <img 
                          src={restaurant.image_url} 
                          alt={restaurant.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <Store className="w-8 h-8 text-purple-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-slate-900">{restaurant.name}</h3>
                        <Badge variant={restaurant.is_active ? 'default' : 'secondary'} className={restaurant.is_active ? 'bg-green-100 text-green-700' : ''}>
                          {restaurant.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mb-2">{restaurant.cuisine_type}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {restaurant.city}
                        </span>
                        {restaurant.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {restaurant.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Joined {formatDate(restaurant.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{restaurant.order_count || 0}</p>
                      <p className="text-xs text-slate-500">Total Orders</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{restaurant.delivery_order_count || 0}</p>
                      <p className="text-xs text-slate-500">Delivery</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {restaurant.rating?.toFixed(1) || '0.0'}
                      </p>
                      <p className="text-xs text-slate-500">Rating</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
